import * as _ from 'lodash';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import { RRule } from 'rrule';
import pako from 'pako';
import { v4 as uuidv4 } from 'uuid';
import * as Constants from './constants';
import {
  DEFAULT_TRAVEL_TIME,
  ERRORS,
  FORMAT_TYPES_ENUM,
  MM_DD_YYYY_FORMAT,
  // EXISTING_CUSTOM_KEYS
} from './constants';
import * as DateUtils from './DateUtils';

import { ATTRIBUTE_TYPE, USER_ROLE, ATTRIBUTE_TYPE_IN_TASK } from './enums';

function getDynamicParentChildEmptyRow() {
  return {
    priorityTemplate: '',
    taskTypeId: '',
    workflowStepId: uuidv4(),
    dependsOn: [],
    autoResolveDependsOn: [],
    sameStartTimeAs: [],
    sameAssigneeAs: '',
    sameLocationAs: '',
    immediateNextTo: '',
    taskTypeChangeAllowed: true,
  };
}

export function capitalizeFirstLetter(string) {
  if (!_.size(string)) {
    return '';
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const elasticSearchUtil = (dynamoResponse, elasticResponse, fieldList) => {
  const dynamoResponseDict = _.keyBy(dynamoResponse, 'id');

  // if user is deleted removing user from the elasticResponse
  if (!_.isEmpty(dynamoResponseDict))
    elasticResponse.filter((e) => dynamoResponseDict[e.id] && !dynamoResponseDict[e.id].isDeleted);
  // if all users are found
  const tempResponse = elasticResponse.map((e) => {
    if (dynamoResponseDict[e.id] && fieldList.includes(dynamoResponseDict[e.id].fieldId)) {
      const returnValue = dynamoResponseDict[e.id];
      dynamoResponseDict[e.id] = {};
      return returnValue;
    }
    return e;
  });
  // if not all users are found
  Object.keys(dynamoResponseDict).forEach((e) => {
    if (!_.isEmpty(dynamoResponseDict[e]) && fieldList.includes(dynamoResponseDict[e].fieldId)) {
      tempResponse.push(dynamoResponseDict[e]);
    }
  });

  const nonDeletedUserList = tempResponse.filter((e) => !e.isDeleted);

  return nonDeletedUserList
    .map((e, i) => {
      if (i < 50) return e;
      return {};
    })
    .filter((e) => !_.isEmpty(e));
};

export function mapTeamsToUsers(teams, users) {
  const teamUserMapping = {};
  users.forEach((user) => {
    const teamIds = (user.teams || []).filter((team) => !team.isDeleted).map((team) => team.teamId);
    const teamsDict = _.keyBy(user.teams, 'teamId');
    teamIds.forEach((id) => {
      teamUserMapping[id] = teamUserMapping[id] || {};
      (user.roles || [])
        .filter((role) => !role.isDeleted)
        .forEach((role) => {
          let roleKey = role.roleId.toLowerCase().replace(/ /g, '');
          if (roleKey === USER_ROLE.FOREMAN) {
            roleKey = USER_ROLE.TEAM_LEADS;
          } else if (roleKey === USER_ROLE.TECHNICIAN || roleKey === USER_ROLE.LEASE_OPERATOR) {
            roleKey = USER_ROLE.TEAM_MEMBERS;
          }
          teamUserMapping[id][roleKey] = teamUserMapping[id][roleKey] || [];

          if (
            (roleKey === USER_ROLE.TEAM_LEADS && teamsDict[id].isTeamLead !== false) ||
            (roleKey === USER_ROLE.TEAM_MEMBERS && teamsDict[id].isTeamMember !== false) ||
            (roleKey !== USER_ROLE.TEAM_LEADS && roleKey !== USER_ROLE.TEAM_MEMBERS)
          ) {
            teamUserMapping[id][roleKey].push({
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              fieldId: user.fieldId,
            });
          }
        });
      if (teamsDict[id].isTeamMember === true) {
        teamUserMapping[id][USER_ROLE.TEAM_MEMBERS] = teamUserMapping[id][USER_ROLE.TEAM_MEMBERS] || [];
        teamUserMapping[id][USER_ROLE.TEAM_MEMBERS].push({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          fieldId: user.fieldId,
        });
      }
      if (teamsDict[id].isTeamLead === true) {
        teamUserMapping[id][USER_ROLE.TEAM_LEADS] = teamUserMapping[id][USER_ROLE.TEAM_LEADS] || [];
        teamUserMapping[id][USER_ROLE.TEAM_LEADS].push({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          fieldId: user.fieldId,
        });
      }
    });
  });
  teams.forEach((team) => {
    const teamUsers = teamUserMapping[team.id];
    Object.keys(teamUsers || {}).forEach((key) => {
      teamUsers[key] = _.uniqBy(teamUsers[key], 'id');
    });
    team.users = teamUsers || {};
  });
}

export function getUserDropDownMapping(item) {
  return {
    value: item.id,
    key: item.id,
    text: `${item.firstName} ${item.lastName}`,
    first: item.firstName, // react is throwing error if we use firstName..that's why kept first
    last: item.lastName,
    teams: item.teams || [],
    originalObject: item,
  };
}

export function mapStopsToArea(areas, stops, stateTeams, users) {
  const teams = { ...stateTeams };
  const areaStopResourceMap = {};
  areas.forEach((area) => {
    areaStopResourceMap[area.id] = {
      area,
      stops: [],
      teams: [],
    };
  });
  stops.forEach((stop) => {
    stop.areas?.forEach((area) => {
      if (areaStopResourceMap[area.areaId]) {
        areaStopResourceMap[area.areaId].stops.push(stop);
      }
    });
  });
  const usersMap = _.keyBy(users, 'id');
  const teamMap = _.keyBy(teams, 'id');

  areas.forEach((area) => {
    area.teams.forEach((team) => {
      areaStopResourceMap[area.id].teams.push({ ...teamMap[team.teamId] });
      if (team.resources) {
        team.resources
          .filter((val) => !val.isDeleted)
          .forEach((resource) => {
            const lastTeamIndex = areaStopResourceMap[area.id].teams.length - 1;
            areaStopResourceMap[area.id].teams[lastTeamIndex][resource.resourceLevel] =
              areaStopResourceMap[area.id].teams[lastTeamIndex][resource.resourceLevel] || [];
            if (!usersMap[resource.userId]) {
              console.error(
                `did not find resource id ${resource.userId} belonging to the area id ${area.id} in the user list`
              );
              return;
            }
            areaStopResourceMap[area.id].teams[lastTeamIndex][resource.resourceLevel].push(usersMap[resource.userId]);
          });
      }
    });
  });
  return areaStopResourceMap;
}

/**
 * Both params of moment data type
 * @param date1
 * @param date2
 * @returns {boolean}
 */
function areBothDatesEqual(date1, date2) {
  return date1.date() === date2.date() && date1.month() === date2.month() && date1.year() === date2.year();
}
function areBothDatesEqualtemp(date1, date2) {
  return date1.date() === date2.date() && date1.month() === date2.month() && date1.year() === date2.year();
}

/**
 *
 * @param dateList can be array of strings
 * @param date will be of moment data type
 * @returns {boolean}
 */
function ifDateListContainsDate(dateList = [], date, test) {
  if (test) {
    for (const d of dateList) {
      if (areBothDatesEqualtemp(date, moment(d))) {
        return true;
      }
    }
  }
  for (const d of dateList) {
    if (areBothDatesEqual(date, moment(d))) {
      return true;
    }
  }
  return false;
}

/**
 * To determine user availability using hitch
 * @param date
 * @param hitch
 * @returns {boolean}
 */
function isUserAvailableForDateUsingHitchPattern(date, user) {
  const { hitch } = user;
  const hitchCopy = _.cloneDeep(hitch);
  if (!hitchCopy.hitchStartDate) return false;
  let totalNoOfDaysInHitch = 0;
  if (hitchCopy.hitchPattern) {
    for (const pattern of hitchCopy.hitchPattern) {
      if (!_.isNil(pattern.hitchWorkDays) && !_.isNil(pattern.hitchOffDays)) {
        totalNoOfDaysInHitch += parseInt(pattern.hitchWorkDays, 10) + parseInt(pattern.hitchOffDays, 10);
      }
    }
    if (totalNoOfDaysInHitch === 0) {
      return false;
    }
    const hitchStartDate = momentTZ.tz(`${hitchCopy.hitchStartDate}`, user.timeZone);
    if (date.isBefore(hitchStartDate)) return false;
    const diffInDays = date.diff(hitchStartDate, 'days'); // difference between the given date and user's hitch start date
    let diff = diffInDays;
    diff %= totalNoOfDaysInHitch;

    for (const pattern of hitchCopy.hitchPattern) {
      if (!_.isNil(pattern.hitchWorkDays) && !_.isNil(pattern.hitchOffDays)) {
        diff -= parseInt(pattern.hitchWorkDays, 10) + parseInt(pattern.hitchOffDays, 10);
        if (diff < 0) {
          // the date belongs to this hitch
          return diff + parseInt(pattern.hitchOffDays, 10) < 0;
        }
      }
    }
  }
  return false;
}

/**
 *if hitch and offDays data are not available, it will be assumed that user is available for every day
 * @param date
 * @param user
 */
export function isUserAvailableForDate(date, user) {
  if ((user.hitch || user.offDays) && user.timeZone) {
    if (!momentTZ.tz.zone(user.timeZone)) {
      return false;
    }

    const eventDate = momentTZ.tz(date, user.timeZone);

    if (user.offDays) {
      if (ifDateListContainsDate(user.offDays, eventDate)) {
        return false;
      }
    }
    if (user.onDays) {
      if (ifDateListContainsDate(user.onDays, eventDate)) {
        return true;
      }
    }
    if (user.hitch) {
      return isUserAvailableForDateUsingHitchPattern(eventDate, user);
    }
  }
  return false;
}

export function getVirtualRecurringTasksFromOriginalTask(originalTask, startDate, endDate) {
  if (!originalTask.isRecurring) throw new Error('Task is not recurring');
  if (!originalTask.schedule) throw new Error('Missing Recurrence information');

  if (!originalTask.schedule.rrule) {
    console.error(`did not find rrule for task id ${originalTask.id}`);
    return [];
  }
  const rruleObject = RRule.fromString(originalTask.schedule.rrule);

  const recurrenceDates = rruleObject.between(startDate, endDate, true);

  const virtualTasks = [];

  recurrenceDates.forEach((recurrenceDate) => {
    const virtualTask = JSON.parse(JSON.stringify(originalTask));
    virtualTask.id = uuidv4();
    virtualTask.sourceRecurringTaskId = originalTask.id;
    virtualTask.originalRecurrenceDate = recurrenceDate.toISOString();
    virtualTask.isVirtual = true;
    virtualTask.isRecurring = false;
    virtualTask.taskNumber = (virtualTask.taskNumber || '').replace(/\d+$/, '#');
    virtualTask.startTime = recurrenceDate.toISOString();

    if (
      originalTask.deletedForRecurrenceDates &&
      originalTask.deletedForRecurrenceDates.indexOf(virtualTask.startTime) !== -1
    )
      return;

    virtualTask.endTime = moment(recurrenceDate).add(originalTask.duration, 'minutes').toISOString();
    virtualTask.createdOn = new Date().toISOString();

    virtualTasks.push(virtualTask);
  });
  return virtualTasks;
}

export function getSearchStopsFilterObject(fieldId, searchStr) {
  return {
    fieldId: {
      match: fieldId,
    },
    type: {
      match: 'stop',
    },
    name: {
      matchPhrasePrefix: searchStr,
    },
  };
}

export function getSearchgraphQLformultiplefieldId(fieldIds) {
  if (fieldIds.length === 0) {
    return {
      filter: {
        fieldId: {
          match: '',
        },
      },
      limit: 10000,
    };
  }
  return {
    filter: {
      or: (fieldIds || []).map((id) => {
        if (id === 'notassignedtofields') {
          return { fieldId: { exists: false } };
        }

        return { fieldId: { match: id } };
      }),
    },
    limit: 10000,
  };
}

export function getSearchGraphQLParamForFieldId(fieldId) {
  return {
    filter: {
      fieldId: {
        match: fieldId,
      },
    },
    limit: 10000,
  };
}

export function getUserSearchGraphQLParamForFieldId(fieldId) {
  return {
    filter: {
      fieldId: {
        match: fieldId,
      },
    },
    sort: {
      field: 'id',
      direction: 'desc',
    },
    limit: 10000,
  };
}

export function createFilterObjForAllRoutesAndFieldId(fieldId) {
  return {
    type: {
      match: 'route',
    },
    fieldId: {
      match: fieldId,
    },
  };
}

/**
 *
 * @param dropDownValues have format of [{key: "key1", text: "text1"}, {key: "key2", text: "text2"}]
 *
 * return {
 *   key1: "text1",
 *   key2: "text2"
 * }
 */
export function createKeyTextMapFromDropDown(dropDownValues) {
  const keyTextMap = {};
  for (const { key, text } of dropDownValues) {
    keyTextMap[key] = text;
  }
  return keyTextMap;
}

export function stripFromTypeName(object) {
  if (!object) return object;
  if (Array.isArray(object)) {
    for (const singleObject of object) {
      // eslint-disable-next-line no-underscore-dangle
      if (singleObject.__typename) {
        // eslint-disable-next-line no-underscore-dangle
        delete singleObject.__typename;
      }
    }
    // eslint-disable-next-line no-underscore-dangle
  } else if (object.__typename) {
    // eslint-disable-next-line no-param-reassign,no-underscore-dangle
    delete object.__typename;
  }
  return object;
}

export async function getAllItems(apiFunction, responseKey, ...apiArgs) {
  const allItems = [];
  let nextToken = null;
  let totalCount = 0;
  do {
    const response = await apiFunction(...apiArgs, nextToken);
    allItems.push(...response.data[responseKey].items);
    nextToken = response.data[responseKey].nextToken;
    totalCount = parseInt(_.get(response, `data.${[responseKey]}.total`, 0));
  } while (nextToken && nextToken < totalCount);
  return allItems;
}

export async function subscribeFunction(
  subFunction,
  requestId,
  mainFunction,
  timeoutPeriod,
  onSuccessCallback,
  onFailedCallback = (e) => {
    console.error(e);
  }
) {
  let setTimeoutSubscription = null;
  const subscription = subFunction(requestId).subscribe(
    (response) => {
      onSuccessCallback(response);
      if (setTimeoutSubscription) clearTimeout(setTimeoutSubscription);
      if (subscription.unsubscribe) subscription.unsubscribe();
    },
    (e) => {
      onFailedCallback(e);
      if (setTimeoutSubscription) clearTimeout(setTimeoutSubscription);
    },
    () => {
      console.debug(`${requestId} subscription completed`);
    }
  );
  setTimeout(mainFunction, 2000);
  setTimeoutSubscription = setTimeout(() => {
    if (subscription.unsubscribe) subscription.unsubscribe();
    onFailedCallback({ success: false, message: ERRORS.SUBSCRIPTION_TIMED_OUT_ERROR });
  }, timeoutPeriod);
}

export function inflateResponse(compressed) {
  if (!compressed) {
    // eslint-disable-next-line no-throw-literal
    throw { success: false, message: ERRORS.DECOMPRESSION_INVALID_INPUT_ERROR };
  }
  const decompressedOutput = pako.inflate(atob(compressed), { to: 'string' });
  return JSON.parse(decompressedOutput);
}

export function getOrdinalNumber(number) {
  if (number % 10 === 1 && number !== 11) {
    return `${number}st`;
  }
  if (number % 10 === 2 && number !== 12) {
    return `${number}nd`;
  }
  if (number % 10 === 3 && number !== 13) {
    return `${number}rd`;
  }
  return `${number}th`;
}

function toRadians(degree) {
  return degree * (Math.PI / 180);
}

function geoDistance(lat1, lng1, lat2, lng2) {
  const RADIUS = 6371 / 1.6; // Radius of the earth in miles

  const lat1Radians = toRadians(lat1);
  const lat2Radians = toRadians(lat2);

  const latDeltaRadians = toRadians(lat2 - lat1);
  const lngDeltaRadians = toRadians(lng2 - lng1);

  const a =
    Math.sin(latDeltaRadians / 2) * Math.sin(latDeltaRadians / 2) +
    Math.cos(lat1Radians) * Math.cos(lat2Radians) * Math.sin(lngDeltaRadians / 2) * Math.sin(lngDeltaRadians / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return RADIUS * c;
}

function travelTime(lat1, lng1, lat2, lng2) {
  const SPEED = 20; // miles/hour
  const distance = geoDistance(lat1, lng1, lat2, lng2);
  return Math.ceil((60 * (distance * 60)) / SPEED);
}

export function getCrowflyMatrix(latlng, crowFlyDistanceFactorPercentage = 0) {
  const dms = {};
  Object.keys(latlng).forEach((e) => {
    if (!dms[e]) {
      dms[e] = {};
    }
    Object.keys(latlng).forEach((j) => {
      const [lat1, lng1] = latlng[e];
      const [lat2, lng2] = latlng[j];
      let travelTimeinSeconds = Math.round(1 + travelTime(lat1, lng1, lat2, lng2));
      if (isNaN(travelTimeinSeconds) || travelTimeinSeconds > DEFAULT_TRAVEL_TIME) {
        travelTimeinSeconds = DEFAULT_TRAVEL_TIME;
      } else if (crowFlyDistanceFactorPercentage && !isNaN(Number(crowFlyDistanceFactorPercentage))) {
        travelTimeinSeconds += Math.round((travelTimeinSeconds * Number(crowFlyDistanceFactorPercentage)) / 100);
      }
      dms[e][j] = travelTimeinSeconds;
    });
  });
  return dms;
}

export const findParentFromOptionList = (attribute, schema) => {
  const parentAttributeOptions = [];
  if (schema) {
    const options = _.clone(schema);
    Object.keys(options).forEach((key) => {
      if (
        options[key].type.toLowerCase() === ATTRIBUTE_TYPE.PICKLIST.key &&
        key !== attribute.key &&
        !(options[key].parentId && options[key].parentId === attribute.key)
      ) {
        parentAttributeOptions.push({
          key,
          value: key,
          text: options[key].name,
        });
      }
    });
  }
  return parentAttributeOptions;
};

export const findCustomAttribute = (schema) => {
  // have to remove these keys as custom attributes from prod
  const schemaArray =
    schema && Object.keys(schema).length
      ? Object.keys(schema)
          .filter(
            (key) => key.startsWith('c_')
            // && EXISTING_CUSTOM_KEYS.indexOf(key) < 0
          )
          .map((key) => ({ key, ...schema[key] }))
          .sort((a, b) => {
            if (a.sequenceNumber === b.sequenceNumber) {
              const displayName1 = (a.displayName || '').toLowerCase();
              const displayName2 = (b.displayName || '').toLowerCase();
              if (displayName1 > displayName2) return 1;
              return -1;
            }
            return b.sequenceNumber - a.sequenceNumber;
          })
      : [];
  return schemaArray;
};

function getPickListOptionObj(option, identifier = 'id') {
  return {
    key: option[`${identifier}`],
    value: option[`${identifier}`],
    text: option.name,
    originalobject: option,
  };
}

export function getOptionIdfromNameForMetadataPicklists(name) {
  return name.replace(/[^\w]/gi, '').toUpperCase();
}

export function getPickListOptions(list) {
  return list.map((option) => getPickListOptionObj(option));
}

export function getPickListOptionObjWithNameAsIdetifier(list, identifier) {
  return list.map((option) => getPickListOptionObj(option, identifier));
}

export function getAllPicklistAsOptions(picklists) {
  return picklists.map((picklist) => ({
    key: picklist.id,
    value: picklist.id,
    text: picklist.displayName,
    originalobject: picklist,
  }));
}

export function parseJSONStringToObject(str) {
  if (typeof str === 'object') return str;
  if (typeof str === 'string') return JSON.parse(str);
  return null;
}

export function userListAccordingToRole(usersInfo) {
  let usersList = (usersInfo || []).map((item) => {
    return getUserDropDownMapping(item);
  });
  usersList = _.uniqBy(usersList, 'key');
  return usersList;
}

export function getMappedEntityChanges(newList, oldList, key = String) {
  const response = {
    added: _.differenceBy(newList, oldList, key),
    deleted: _.differenceBy(oldList, newList, key),
    unchanged: _.intersectionBy(newList, oldList, key),
  };
  return response;
}

export function areaTableData(teams, area, stops, rowSpanAreaMap = {}) {
  const tableData = [];
  teams.forEach((team) => {
    let primary = null;
    let secondary = null;
    if (team.primary) {
      primary = team.primary.map((user) => `${user.firstName} ${user.lastName}`).join(', ');
    }
    if (team.secondary) {
      secondary = team.secondary.map((user) => `${user.firstName} ${user.lastName}`).join(', ');
    }
    tableData.push({
      areaName: rowSpanAreaMap[area.id] ? null : area.name,
      areaId: area.id,
      type: area.areaType,
      stops,
      teamName: team.name,
      primary,
      secondary,
      modifiedOn: DateUtils.convertUTCDateStringToYYYYMMDDHHMMSSFormat(area.modifiedOn),
    });
    rowSpanAreaMap[area.id] = rowSpanAreaMap[area.id] || 0;
    // eslint-disable-next-line no-plusplus
    rowSpanAreaMap[area.id]++;
  });
  return tableData;
}

export function formatUsersForAreaTable(teams) {
  const clonedTeams = _.cloneDeep(teams);
  clonedTeams.forEach((team) => {
    const userListDict = _.keyBy(team.userList, 'key');
    team.primary = team.primary?.map((userId) => _.get(userListDict, `[${userId}].user`)).filter((val) => val);
    team.secondary = team.secondary?.map((userId) => _.get(userListDict, `[${userId}].user`)).filter((val) => val);
  });
  return clonedTeams;
}

export const getMapUrl = (googleMapsKey) => {
  return `https://maps.googleapis.com/maps/api/js?key=${googleMapsKey}&v=3.exp&libraries=geometry,drawing,places`;
};

export function getSequentialWorkflowStepForTaskType(taskType) {
  const workflow = taskType.workflowVersions?.find((wfv) => wfv.version === Constants.CURRENT_WORKFLOW_VERSION);
  if (!workflow) return null;
  const { workflowSteps } = workflow;
  const clonedWorkflowSteps = _.cloneDeep(workflowSteps);
  let parentDependencies = [];
  let k = 0;
  while (clonedWorkflowSteps.length > 0 && k < 100) {
    k += 1;
    const group = [];
    for (let i = 0; i < clonedWorkflowSteps.length; i += 1) {
      const workflowStep = clonedWorkflowSteps[i];
      if (workflowStep.dependsOn && workflowStep.dependsOn.length === 0) {
        group.push(workflowStep.workflowStepId);
        clonedWorkflowSteps.splice(i, 1);
        i -= 1;
      } else {
        let eligibleForParentDependencies = true;
        for (const dependency of workflowStep.dependsOn) {
          if (!parentDependencies.includes(dependency)) {
            eligibleForParentDependencies = false;
            break;
          }
        }
        if (eligibleForParentDependencies) {
          group.push(workflowStep.workflowStepId);
          clonedWorkflowSteps.splice(i, 1);
          i -= 1;
        }
      }
    }
    parentDependencies = [...parentDependencies, ...group];
  }
  return parentDependencies;
}

export function getActionTypes(reduxName) {
  return function (actions) {
    const actionObject = {};
    actions.forEach((actionStr) => {
      actionObject[actionStr] = `${reduxName}/${actionStr}`;
    });
    return actionObject;
  };
}

export function getFileExtension(fileName) {
  if (!fileName) {
    return '';
  }
  const fileNamedSplitByDot = fileName.split('.');
  if (fileNamedSplitByDot.length === 1) return '';
  return `.${fileNamedSplitByDot[fileNamedSplitByDot.length - 1]}`;
}

export function getFileName(key) {
  if (!key) {
    return '';
  }
  const keySplitBySlash = key.split('/');
  return keySplitBySlash[keySplitBySlash.length - 1];
}

export function download(url) {
  const element = document.createElement('a');
  element.setAttribute('href', url);
  element.setAttribute('target', '_blank');

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

export function isDateInBetweenDateRange({ date, startDate, endDate }) {
  return moment(date).isBetween(startDate, endDate, 'days', '[]');
}

export function filterFilledParentPicklistObject(list) {
  return (list || []).filter((val) => {
    const { type, entity, attributeName, picklistId, elementId } = val;
    if (type === 'metadata' && entity && attributeName && picklistId) {
      return true;
    }
    if (type === 'element' && elementId) {
      return true;
    }
    return false;
  });
}

export const mapParentPicklistErrorToField = ({ formValues }) => {
  const error = [];
  const parentPicklists = _.get(formValues, 'parentPickListIds') || [];
  const message = 'Please select a value';
  parentPicklists.forEach((part, index) => {
    const errObj = {};
    if (part.entity && !part.attributeName) {
      errObj.attributeName = message;
    }
    if (part.entity && !part.picklistId) {
      errObj.picklistId = message;
    }
    if (!part.entity && (part.attributeName || part.picklistId)) {
      errObj.entity = message;
    }
    if (Object.keys(errObj).length !== 0) {
      error[index] = errObj;
    }
  });
  return { isValid: error.length === 0, error };
};

export const mapDynamicPicklistErrorToField = ({ formValues }) => {
  const { path, entity, elementId, subPath } = _.get(formValues, 'metadataInfo') || {};
  const message = 'Please select a value';
  const metadataInfoError = {};
  if (_.get(formValues, 'attributeType') === 'metadatadynamiclist') {
    if (!entity) {
      metadataInfoError.entity = message;
    }
    if (!path) {
      metadataInfoError.path = message;
    }
    if (path === 'customAttribute' && !elementId) {
      metadataInfoError.elementId = message;
    }
    if (path === 'vendor' && !subPath) {
      metadataInfoError.subPath = message;
    }
    if (path === 'contact' && !subPath) {
      metadataInfoError.subPath = message;
    }
  }

  return { isValid: _.size(metadataInfoError) === 0, error: metadataInfoError };
};

export const findSelectedEntity = (formValues, entity, params = {}) => {
  const { useParentTaskType, parentTask, selectedTasktype, sectionId } = params;

  if (entity === 'tasktype') {
    if (useParentTaskType && parentTask) {
      return parentTask?.taskTypeId;
    }
    const workflowsteps = selectedTasktype?.originalobject?.workflowVersions?.[0]?.workflowSteps ?? [];
    const isSelectedTaskTypeParent = workflowsteps?.length;
    if (!useParentTaskType && isSelectedTaskTypeParent && sectionId) {
      const reqWfStep = workflowsteps.find((step) => step.formSectionId === sectionId);
      return reqWfStep?.taskTypeId;
    }
    return formValues.s_tasktype;
  }
  if (entity === 'asset') {
    return formValues.s_asset || formValues.s_stop;
  }
  if (entity === 'assignee') {
    // change required here
    return formValues.s_assigneeid;
  }
  return null;
};

export const findRequiredEntities = ({
  entity,
  selectedEntity,
  taskTypeOptions,
  assetOptions,
  stopOptions,
  assigneeOptions,
  selectedTasktype,
  path,
  attributeObj,
  ...props
}) => {
  if (entity === 'task') {
    if (path === 'assigneeList') {
      return assigneeOptions.items;
    }
    if (path === 'assetList') {
      const reqPicklist = props?.searchPicklistForForm?.[attributeObj.id];
      return getPickListOptions(Object.values(reqPicklist?.options || {}) ?? []);
    }
    return [];
  }
  if (entity === 'tasktype') {
    if (props.useParentTaskType && props?.parentTask?.taskTypeId && props?.parentTask?.taskTypeId === selectedEntity) {
      const parentTaskType = props?.parentTask?.taskType;
      return [
        { value: parentTaskType.id, key: parentTaskType.id, text: parentTaskType.name, originalobject: parentTaskType },
      ];
    }
    return taskTypeOptions.items.filter(({ originalobject }) => originalobject.id === selectedEntity);
  }
  if (entity === 'assignee') {
    const taskType =
      taskTypeOptions.items.find(({ originalobject }) => originalobject.id === props?.formValues?.s_tasktype) ||
      selectedTasktype;
    if (taskType?.originalobject?.needEquipment) {
      const x = assigneeOptions.items.filter(
        ({ originalobject }) => selectedEntity.includes(originalobject.id) && !originalobject?.isEquipmentResource
      );
      return x;
    }
    return assigneeOptions.items.filter(({ originalobject }) => selectedEntity.includes(originalobject.id));
  }
  if (entity === 'asset') {
    return [..._.get(assetOptions, 'items', []), ..._.get(stopOptions, 'items', [])].filter(
      ({ originalobject }) => originalobject.id === selectedEntity
    );
  }
  return null;
};

export const findRequiredOptionForMetatdata = ({ picklistDict, picklistId, reqEntities, attributeName }) => {
  const reqPicklistOptions = picklistDict[picklistId] && picklistDict[picklistId].options;
  let reqOption = null;
  const reqEntityKeyed = _.keyBy(reqEntities, (ent) => {
    return ent.originalobject[attributeName];
  });
  for (const option of Object.values(reqPicklistOptions || [])) {
    if (reqEntityKeyed[option.name]) {
      reqOption = option;
      break;
    }
  }
  return reqOption;
};

const getValue = (valObj, val) => {
  if (!valObj.val) {
    valObj.val = [];
  }
  valObj.val.push(val);
};

export const findValueInAnyParentElement = ({ formValues, parent, valObj = {}, attributeType, ...props }) => {
  const { picklistDict, picklistSchema, taskformValues, entType } = props;
  const { elementId, picklistId, attributeName, entity, useParentTaskType = false } = parent;
  if (
    elementId &&
    (attributeType === ATTRIBUTE_TYPE_IN_TASK.TASKTYPE_ATTRIBUTE || entType === ATTRIBUTE_TYPE_IN_TASK.FORM_ATTRIBUTE)
  ) {
    const reqSchema = picklistSchema[elementId];
    const { parentPickListIds = [], sectionId } = reqSchema;
    let val = formValues[elementId];
    if (!val && entType === ATTRIBUTE_TYPE_IN_TASK.FORM_ATTRIBUTE) {
      val = _.get(reqSchema, 'value');
    }
    if (val) {
      if (
        attributeType === ATTRIBUTE_TYPE_IN_TASK.FORM_ATTRIBUTE ||
        entType === ATTRIBUTE_TYPE_IN_TASK.FORM_ATTRIBUTE
      ) {
        const reqPicklistOptions = _.get(picklistSchema, `${elementId}.options`) || [];
        
        // Handle both single values and multiple selection (array)
        const values = Array.isArray(val) ? val : [val];        
        for (const singleVal of values) {
          const reqOpt = reqPicklistOptions.find((opt) => opt.value === singleVal);
          if (reqOpt) {
            getValue(valObj, reqOpt.id);
          }
        }
      } else {
        // Handle both single values and multiple selection (array)
        const values = Array.isArray(val) ? val : [val];
        for (const singleVal of values) {
          getValue(valObj, singleVal);
        }
      }
    }
    for (const parentObj of parentPickListIds || []) {
      findValueInAnyParentElement({
        formValues,
        parent: parentObj,
        valObj,
        ...props,
        sectionId,
      });
    }
  } else if (elementId && attributeType === ATTRIBUTE_TYPE_IN_TASK.GLOBAL_ATTRIBUTE) {
    for (const [key, value] of Object.entries(picklistSchema)) {
      const { parentPickListIds = [] } = value;
      if (key.startsWith('g_') && value.id === elementId) {
        const val = formValues[key];
        if (val) {
          
          // Handle both single values and multiple selection (array)
          const values = Array.isArray(val) ? val : [val];
          for (const singleVal of values) {
            getValue(valObj, singleVal);
          }
        }
        for (const parentObj of parentPickListIds || []) {
          findValueInAnyParentElement({
            formValues,
            parent: parentObj,
            valObj,
            attributeType,
            ...props,
          });
        }
      }
    }
  }
  if (entity) {
    const selectedEntity = findSelectedEntity(
      entType === ATTRIBUTE_TYPE_IN_TASK.FORM_ATTRIBUTE ? taskformValues : formValues,
      entity,
      {
        useParentTaskType,
        parentTask: props.parentTask,
        sectionId: props.sectionId,
        selectedTasktype: props.selectedTasktype,
      }
    );
    const reqEntities = findRequiredEntities({
      entity,
      selectedEntity,
      ...props,
      useParentTaskType,
    });
    if (reqEntities) {
      const reqOption = findRequiredOptionForMetatdata({ picklistDict, picklistId, reqEntities, attributeName });
      if (reqOption) {
        getValue(valObj, reqOption.id);
      }
    }
  }

  return valObj;
};

export const findUniqueOptionsIds = ({ reqMapping, pId, reqOption }) => {
  const picklistIdIndex = reqMapping.picklistIds.findIndex((id) => id === pId);
  const filteredOptions = reqMapping.mappingTable.filter((val) => val.includes(reqOption.id));
  const optionIds = _.uniq(filteredOptions.map((option) => option[picklistIdIndex]));
  return optionIds;
};

export const findUniqueOptionsIdsForElementType = ({ reqMapping, pId, val }) => {
  const picklistIdIndex = reqMapping.picklistIds.findIndex((id) => id === pId);
  
  // Use OR logic: find rows that contain ANY of the parent IDs
  // This allows multiple selection to show parts from Rig 1 OR Rig 2, etc.
  const filteredOptions = reqMapping.mappingTable.filter((mapping) => {
    return val.some((v) => mapping.includes(v));
  });
  const optionIds = _.uniq(filteredOptions.map((option) => option[picklistIdIndex]));
  return optionIds;
};

export const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const getUniqueOptionsForDynamicList = ({ reqEntities, path, reqItem, subPath, entity }) => {
  if (entity === 'task') {
    return reqEntities.map(({ text, value }) => {
      return { id: value, key: value, text, value };
    });
  }
  let actualPath = path;
  if (subPath) {
    actualPath = `${path}.${subPath}`;
  }
  const optionsList = reqEntities
    .map((reqEntity) => {
      const reqVal = reqItem
        ? _.get(reqEntity, `originalobject.${path}.${reqItem}`)
        : _.get(reqEntity, `originalobject.${actualPath}`);
      return { text: reqVal, value: reqVal, key: reqVal, id: reqVal };
    })
    .filter(({ value }) => value);
  return _.uniqBy(optionsList, 'value');
};

export const findElementValueAndPicklistId = ({ formValues, elementId, picklistSchema }) => {
  let val = formValues[elementId];
  let picklistId = val ? picklistSchema[elementId].picklistId : '';

  if (!val) {
    for (const [key, value] of Object.entries(picklistSchema)) {
      if (key.startsWith('g_') && value.id === elementId) {
        val = formValues[key];
        picklistId = value.picklistId;
      }
    }
  }
  return { val, picklistId };
};

export const evaluateEngineSettings = ({
  scoreCoefficient,
  timeLimit,
  highScoreThreshold,
  highScoreCoefficient,
  enableClustering,
  enableGroupClustering,
  unassignUsers,
  balanceTechTimeCoefficient,
  useCrowFlyDistances,
  disableTechEndLocation,
  crowFlyDistanceFactorPercentage,
  balanceTechShiftUtilization,
  useUserLocation,
  disableAvailabilityRestriction,
  timeBuffer,
  startTimeLte,
  autoFreezTimeLte,
  startDateLte,
}) => {
  return {
    scoreCoefficient: isNaN(parseFloat(scoreCoefficient)) ? 0 : parseFloat(scoreCoefficient),
    highScoreThreshold: isNaN(parseFloat(highScoreThreshold)) ? 0 : parseFloat(highScoreThreshold),
    highScoreCoefficient: isNaN(parseFloat(highScoreCoefficient)) ? 0 : parseFloat(highScoreCoefficient),
    timeLimit: isNaN(parseFloat(timeLimit)) ? 0 : parseFloat(timeLimit),
    enableClustering: !!enableClustering,
    enableGroupClustering: !!enableGroupClustering,
    unassignUsers: !!unassignUsers,
    balanceTechTimeCoefficient: isNaN(parseFloat(balanceTechTimeCoefficient))
      ? 0
      : parseFloat(balanceTechTimeCoefficient),
    useCrowFlyDistances: !!useCrowFlyDistances,
    crowFlyDistanceFactorPercentage: isNaN(parseFloat(crowFlyDistanceFactorPercentage))
      ? 0
      : parseFloat(crowFlyDistanceFactorPercentage),
    disableTechEndLocation: !!disableTechEndLocation,
    balanceTechShiftUtilization: !!balanceTechShiftUtilization,
    useUserLocation: !!useUserLocation,
    disableAvailabilityRestriction: !!disableAvailabilityRestriction,
    timeBuffer: isNaN(parseFloat(timeBuffer)) ? 0 : parseFloat(timeBuffer),
    startTimeLte: startTimeLte || '',
    autoFreezTimeLte: autoFreezTimeLte || '',
    startDateLte: startDateLte || '',
  };
};

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function mergeAssigneesForNeedsEquipmentTaskType(assigneeIds, originalAssigneeIds, users) {
  const [newAssignee] = assigneeIds;
  const originalAssigneeIdsCopy = JSON.parse(JSON.stringify(originalAssigneeIds));
  originalAssigneeIdsCopy.forEach((item) => {
    item.isDeleted = true;
  });
  const originalAssigneeIdsCopyDict = _.keyBy(originalAssigneeIdsCopy, 'assigneeId');
  const finalAssigneeIds = _.unionBy(assigneeIds, originalAssigneeIdsCopy, 'assigneeId');
  const usersDict = _.keyBy(users, 'id');
  const operatorsToAdd = [];
  finalAssigneeIds.forEach((item) => {
    const { assigneeId, sequence, vendorId } = item;
    if (newAssignee?.assigneeId === assigneeId) {
      const existingOperatorId = originalAssigneeIdsCopyDict[assigneeId]?.operatorId;
      if (existingOperatorId) {
        item.operatorId = existingOperatorId;
        const operator = finalAssigneeIds.find((a) => a.assigneeId === existingOperatorId);
        if (operator) {
          operator.isDeleted = false;
          operator.sequence = sequence;
        }
      } else {
        const user = usersDict[assigneeId];
        if (user && user.isEquipmentResource && user.operatorId) {
          item.operatorId = user.operatorId;
          const operator = finalAssigneeIds.find((a) => a.assigneeId === user.operatorId);
          if (operator) {
            operator.isDeleted = false;
            operator.sequence = sequence;
          } else {
            operatorsToAdd.push({
              assigneeId: user.operatorId,
              sequence,
              isDeleted: false,
              vendorId,
            });
          }
        }
      }
    }
  });

  return _.unionBy(operatorsToAdd, finalAssigneeIds, 'assigneeId');
}

export function getTemplateForDynamicParentChild(taskType, childTasks, includeEmptyRow) {
  const template = [];
  const childTasksIdWorkflowStepIdMap = {};

  for (const childTask of childTasks) {
    const { id, workflowStepId } = childTask;
    childTasksIdWorkflowStepIdMap[id] = workflowStepId;
  }

  for (const childTask of childTasks) {
    const {
      workflowStepId,
      dependsOn = [],
      autoResolveDependsOn = [],
      sameStartTimeAs = [],
      sameAssigneeAs,
      sameLocationAs,
      immediateNextTo,
      taskTypeId,
      priorityTemplate,
    } = childTask;

    template.push({
      workflowStepId,
      taskTypeId,
      priorityTemplate,
      dependsOn: (dependsOn || []).map((id) => childTasksIdWorkflowStepIdMap[id]),
      autoResolveDependsOn: (autoResolveDependsOn || []).map((id) => childTasksIdWorkflowStepIdMap[id]),
      sameStartTimeAs: (sameStartTimeAs || []).map((id) => childTasksIdWorkflowStepIdMap[id]),
      sameAssigneeAs: sameAssigneeAs ? childTasksIdWorkflowStepIdMap[sameAssigneeAs] : '',
      sameLocationAs: sameLocationAs ? childTasksIdWorkflowStepIdMap[sameLocationAs] : '',
      immediateNextTo: immediateNextTo ? childTasksIdWorkflowStepIdMap[immediateNextTo] : '',
    });
  }
  if (includeEmptyRow) {
    template.push(getDynamicParentChildEmptyRow());
  }
  return template;
}

export const getDateRange = ({ number, text }, formatType) => {
  const startDate = moment().subtract(number, text).startOf('day');
  const endDate = moment().endOf('day'); // moment().subtract(1, 'day').endOf('day');
  return {
    // eslint-disable-next-line no-nested-ternary
    startDate: formatType
      ? formatType === FORMAT_TYPES_ENUM.ISOSTRING
        ? startDate.toISOString()
        : startDate.format(MM_DD_YYYY_FORMAT)
      : startDate,
    // eslint-disable-next-line no-nested-ternary
    endDate: formatType
      ? formatType === FORMAT_TYPES_ENUM.ISOSTRING
        ? endDate.toISOString()
        : endDate.format(MM_DD_YYYY_FORMAT)
      : endDate,
  };
};

export const openTaskInANewTab = (taskId) => {
  window.open(`taskViewPage/task/update/${taskId}`, '_blank');
};

export const zlibDeflate = (inflatedString) => {
  const strData = atob(inflatedString);

  // split it into an array rather than a "string"
  const charData = strData.split('').map(function (x) {
    return x.charCodeAt(0);
  });

  // convert to binary
  // const binData = new Uint8Array(charData);

  // inflate
  const inflatedDate = pako.ungzip(charData);
  const stringRes = new TextDecoder().decode(inflatedDate);
  const output = JSON.parse(stringRes);
  return output;
};
